import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import { Analytics } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import { useAutoList } from '../hooks/useAutoList';
import { isGreetingActive } from '../utils/birthday';

// Композер поздравления клиента с Днём рождения — облегчённая версия редактора
// «Настройки → Коммуникации → Поздравление».
// Можно: поправить обращение (Имя Фамилия → Имя), добавить личное сообщение
// (видно в превью, куда вставится), включить подарок с полями как в Настройках.
// Защита от дубля: если клиент уже поздравлен в этом году — баннер + подтверждение.
export default function BirthdayGreetingModal({ client, onClose, onSent }) {
  const { t, i18n } = useTranslation();
  const msgRef = useAutoList('birthday_message');

  const [lang, setLang] = useState(i18n.language === 'en' ? 'en' : 'ru');
  const [name, setName] = useState(client.name || '');
  const [customMessage, setCustomMessage] = useState('');
  const [signature, setSignature] = useState('');
  const [promoOn, setPromoOn] = useState(false); // подарок по умолчанию выключен
  const [ctaOn, setCtaOn] = useState(false);     // кнопка действия по умолчанию выключена
  const [fromSelf, setFromSelf] = useState(false); // отправить от ящика админа
  const [senderInfo, setSenderInfo] = useState(null);
  const [promoText, setPromoText] = useState('');
  const [promoNote, setPromoNote] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [giftDefaults, setGiftDefaults] = useState({ promo_text: '', promo_note: '', promo_code: 'HB-2026' });
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const greetedThisYear = isGreetingActive(client.birthday, client.birthday_greeted_year, client.birthday_greeted_at);

  // Знаковая разница в днях до ближайшего ДР (учёт перехода через год).
  const bdDiff = (() => {
    if (!client.birthday) return null;
    const [, mm, dd] = String(client.birthday).split('-');
    if (!mm || !dd) return null;
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const y = now.getFullYear();
    let best = null;
    for (const yr of [y - 1, y, y + 1]) {
      const d = new Date(yr, Number(mm) - 1, Number(dd)); d.setHours(0, 0, 0, 0);
      const diff = Math.round((d - now) / 86400000);
      if (best === null || Math.abs(diff) < Math.abs(best)) best = diff;
    }
    return best;
  })();
  const isBirthdayToday = bdDiff === 0;

  // Цветной тег относительной даты. Сегодня — зелёный, будущее — синий, прошлое — янтарный.
  const relTag = (() => {
    if (bdDiff == null) return null;
    const named = {
      '0':  { label: t('birthdayGreeting.relToday'),           tone: 'green' },
      '1':  { label: t('birthdayGreeting.relTomorrow'),        tone: 'blue' },
      '2':  { label: t('birthdayGreeting.relDayAfter'),        tone: 'blue' },
      '-1': { label: t('birthdayGreeting.relYesterday'),       tone: 'amber' },
      '-2': { label: t('birthdayGreeting.relBeforeYesterday'), tone: 'amber' },
    };
    const hit = named[String(bdDiff)];
    const label = hit ? hit.label
      : bdDiff > 0 ? t('birthdayGreeting.relInDays', { count: bdDiff })
      : t('birthdayGreeting.relDaysAgo', { count: Math.abs(bdDiff) });
    const tone = hit ? hit.tone : (bdDiff > 0 ? 'blue' : 'amber');
    const palette = {
      green: { bg: '#e6f6ec', fg: '#1a7f43' },
      blue:  { bg: '#e7f0fd', fg: '#1f6feb' },
      amber: { bg: '#fdf0e3', fg: '#b9700f' },
    }[tone];
    return { label, ...palette };
  })();

  useEffect(() => {
    Analytics.birthdayGreetingOpened({ client_id: client.id, already_greeted: !!greetedThisYear });
    api.getBirthdayGreetingSenderInfo(client.id).then(setSenderInfo).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Прячем Action-FAB, пока открыт модал.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  // Дефолты подарка из настроек орги (как в Коммуникациях): эффективные значения
  // (оверрайд тенанта → дефолт шаблона). Подтягиваем при смене языка.
  useEffect(() => {
    let cancelled = false;
    api.getEmailTexts({ template: 'birthday', lang })
      .then(res => {
        if (cancelled) return;
        const d = res.defaults || {}; const o = res.overrides || {}; const b = res.blocks || {};
        const eff = (f) => (o[f] != null && o[f] !== '') ? o[f] : (d[f] || '');
        const gd = {
          promo_text: eff('promo_text'),
          promo_note: eff('promo_note'),
          promo_code: (b.promo_code != null && b.promo_code !== '') ? b.promo_code : 'HB-2026',
        };
        setGiftDefaults(gd);
        // Префилл редактируемых полей дефолтами текущего языка.
        setPromoText(gd.promo_text); setPromoNote(gd.promo_note); setPromoCode(gd.promo_code);
        // Подпись — из основного шаблона (оверрайд тенанта → дефолт).
        setSignature(eff('signature'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  // Дебаунс-загрузка превью при изменении полей. Перезагружаем iframe только если
  // HTML реально изменился — иначе Safari зря переparsит sandbox-документ (тормоза).
  const debounceRef = useRef(null);
  const loadPreview = useCallback(() => {
    setPreviewLoading(true);
    api.getBirthdayGreetingPreview(client.id, {
      lang, client_name: name, custom_message: customMessage, signature,
      promo: promoOn,
      promo_text: promoOn ? promoText : '',
      promo_note: promoOn ? promoNote : '',
      promo_code: promoOn ? promoCode : '',
      cta: ctaOn,
    })
      .then(r => setPreviewHtml(prev => (prev === (r.html || '') ? prev : (r.html || ''))))
      .catch(() => setPreviewHtml(''))
      .finally(() => setPreviewLoading(false));
  }, [client.id, lang, name, customMessage, signature, promoOn, promoText, promoNote, promoCode, ctaOn]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadPreview, 650);
    return () => clearTimeout(debounceRef.current);
  }, [loadPreview]);

  const doSend = async (force) => {
    setSending(true);
    try {
      const r = await api.sendBirthdayGreeting(client.id, {
        lang, client_name: name, custom_message: customMessage, signature,
        promo: promoOn,
        promo_text: promoOn ? promoText : '',
        promo_note: promoOn ? promoNote : '',
        promo_code: promoOn ? promoCode : '',
        cta: ctaOn,
        from_self: fromSelf,
        force,
      });
      Analytics.birthdayGreetingSent({ client_id: client.id, lang, forced: !!force, promo: promoOn });
      toast.success(t('birthdayGreeting.sentToast', { email: r.sent_to }));
      onSent && onSent(r);
      onClose();
    } catch (e) {
      if (e.code === 'already_greeted') {
        Analytics.birthdayGreetingDuplicateWarned({ client_id: client.id });
        const who = e.greeted_by_name || t('birthdayGreeting.someone');
        if (window.confirm(t('birthdayGreeting.confirmDuplicate', { name: who }))) {
          doSend(true);
          return;
        }
      } else {
        toast.error(e.message || t('birthdayGreeting.sendError'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (greetedThisYear && !window.confirm(
      t('birthdayGreeting.confirmDuplicate', {
        name: client.birthday_greeted_by_name || t('birthdayGreeting.someone'),
      })
    )) return;
    doSend(!!greetedThisYear);
  };

  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.5px',
  };
  const hintStyle = { fontSize: 12, color: 'var(--text3)', marginTop: 4 };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }); }
    catch { return iso; }
  };

  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal" style={{ maxWidth: 900, width: '94%' }}>
        <div className="modal-header">
          <span>🎂 {t('birthdayGreeting.title', { name: client.name })}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ paddingTop: 12 }}>
          {/* Явная дата ДР клиента + цветной тег относительной даты */}
          {client.birthday && (
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)' }}>
                🎂 {t('birthdayGreeting.birthdayOn', { date: fmtDate(client.birthday) })}
              </span>
              {relTag && (
                <span style={{
                  display: 'inline-block', padding: '4px 12px', borderRadius: 999,
                  fontSize: 13, fontWeight: 700, lineHeight: 1.2,
                  background: relTag.bg, color: relTag.fg,
                }}>
                  {relTag.label}
                </span>
              )}
            </div>
          )}

          {/* Предупреждение: уже поздравлен в этом году */}
          {greetedThisYear && (
            <div style={{
              marginBottom: 16, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(245,158,11,0.16)', border: '1px solid var(--orange, #f5a623)',
              color: 'var(--text)', fontSize: 13, lineHeight: 1.5,
            }}>
              ⚠️ {t('birthdayGreeting.alreadyWarning', {
                name: client.birthday_greeted_by_name || t('birthdayGreeting.someone'),
                date: fmtDate(client.birthday_greeted_at),
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {/* Левая колонка — параметры */}
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              {/* Язык */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('birthdayGreeting.language')}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {['ru', 'en'].map(l => (
                    <button key={l} type="button"
                      className={`btn btn-sm ${lang === l ? 'btn-green' : 'btn-ghost'}`}
                      onClick={() => setLang(l)}>
                      {l === 'ru' ? 'RU' : 'EN'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Обращение (имя) */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('birthdayGreeting.name')}</div>
                <input
                  className="form-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={120}
                  style={{ marginTop: 6 }}
                />
                <div style={hintStyle}>{t('birthdayGreeting.nameHint')}</div>
              </div>

              {/* Личное сообщение */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('birthdayGreeting.customMessage')}</div>
                <textarea
                  className="form-textarea"
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  ref={msgRef}
                  data-ph-capture-attribute-field="birthday_message"
                  rows={4}
                  maxLength={2000}
                  placeholder={t('birthdayGreeting.customMessagePlaceholder')}
                  style={{ marginTop: 6, overflowX: 'hidden' }}
                />
                <div style={hintStyle}>💬 {t('birthdayGreeting.customMessageHint')}</div>
              </div>

              {/* Подпись (предзаполнена из основного шаблона; многострочная) */}
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>{t('birthdayGreeting.signature')}</div>
                <textarea
                  className="form-textarea"
                  value={signature}
                  onChange={e => setSignature(e.target.value)}
                  rows={2}
                  maxLength={200}
                  style={{ marginTop: 6, minHeight: 60, overflowX: 'hidden' }}
                />
              </div>

              {/* Подарок (по умолчанию выключен) */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={promoOn} onChange={e => setPromoOn(e.target.checked)} />
                  <span style={{ fontWeight: 600 }}>{t('birthdayGreeting.giftBlock')}</span>
                </label>
                {promoOn && (
                  <div style={{
                    marginTop: 12, padding: '14px 16px', borderRadius: 8,
                    background: 'var(--bg2)', border: '1px solid var(--border)',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}>
                    <div>
                      <div style={labelStyle}>{t('birthdayGreeting.giftText')}</div>
                      <input className="form-input" type="text" value={promoText} onChange={e => setPromoText(e.target.value)}
                        maxLength={120} placeholder={giftDefaults.promo_text}
                        style={{ marginTop: 6 }} />
                    </div>
                    <div>
                      <div style={labelStyle}>{t('birthdayGreeting.giftCode')}</div>
                      <input className="form-input" type="text" value={promoCode} onChange={e => setPromoCode(e.target.value)}
                        maxLength={40} placeholder={giftDefaults.promo_code}
                        style={{ marginTop: 6 }} />
                    </div>
                    <div>
                      <div style={labelStyle}>{t('birthdayGreeting.giftNote')}</div>
                      <input className="form-input" type="text" value={promoNote} onChange={e => setPromoNote(e.target.value)}
                        maxLength={120} placeholder={giftDefaults.promo_note}
                        style={{ marginTop: 6 }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Кнопка действия (по умолчанию выключена) */}
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={ctaOn} onChange={e => setCtaOn(e.target.checked)} />
                  <span style={{ fontWeight: 600 }}>{t('birthdayGreeting.ctaBlock')}</span>
                </label>
              </div>

              {/* Отправитель: с моего ящика (если у админа есть email) */}
              {senderInfo?.self_email && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={fromSelf} onChange={e => setFromSelf(e.target.checked)} />
                    <span style={{ fontWeight: 600 }}>{t('birthdayGreeting.fromSelf')}</span>
                  </label>
                  <div style={hintStyle}>
                    {fromSelf
                      ? (senderInfo.eligible
                          ? `✉️ ${t('birthdayGreeting.fromSelfOn', { from: senderInfo.self_from })}`
                          : `⚠️ ${t('birthdayGreeting.fromSelfFallback', { email: senderInfo.self_email, from: senderInfo.default_from })}`)
                      : t('birthdayGreeting.fromSelfHint', { from: senderInfo.default_from })}
                  </div>
                </div>
              )}
            </div>

            {/* Правая колонка — превью */}
            <div style={{ flex: '1 1 340px', minWidth: 300 }}>
              <div style={{ ...labelStyle, marginBottom: 6 }}>{t('birthdayGreeting.preview')}</div>
              <div style={{
                border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
                height: 460, background: '#fff', position: 'relative',
              }}>
                {previewLoading && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--text3)', fontSize: 13,
                  }}>{t('birthdayGreeting.previewLoading')}</div>
                )}
                <iframe
                  title="preview"
                  sandbox=""
                  srcDoc={previewHtml}
                  style={{ width: '100%', height: '100%', border: 0 }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-green" onClick={handleSendClick} disabled={sending}>
            {sending ? t('birthdayGreeting.sending') : t('birthdayGreeting.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
